import React, { useState, useMemo, memo } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator } from "react-native";
import { Image } from "expo-image";
import Svg, { Path, Circle } from "react-native-svg";
import { router, useFocusEffect } from "expo-router";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { formatCurrency, getCategoryConfig } from "@/utils/myMoney";
import { formatDate, formatRelativeDate } from "@/utils/date";
import { showSuccess, showError, showConfirm } from "@/utils/toast";
import useFinanceDataStore from "@/store/financeData";
import { useBrandLogo } from "@/hooks/useBrandLogo";
import { AccountSummary, Transaction } from "@/types/financeData";
import { SubscriptionSetupType } from "@/app/my-money/setup";
import Icons from "@/assets/icons";
import SubscriptionServices from "@/services/subscriptionServices";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import FBTrackingService from "@/services/fbTrackingService";

const getInitialLetter = (name: string) =>
  (name || "?").charAt(0).toUpperCase();

// Plus icon for Add more button
const PlusIcon = () => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 5v14M5 12h14"
      stroke="#0F9129"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// More options icon (3 dots)
const MoreIcon = () => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
    <Circle cx={12} cy={6} r={1.5} fill="#161D14" />
    <Circle cx={12} cy={12} r={1.5} fill="#161D14" />
    <Circle cx={12} cy={18} r={1.5} fill="#161D14" />
  </Svg>
);

// Chevron icon for expandable sections
const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <View style={[styles.chevronContainer, isOpen && styles.chevronOpen]}>
    <Icons.ChevronRight width={16} height={16} color="#0F9129" />
  </View>
);

// Account row item
interface AccountRowProps {
  account: AccountSummary;
  onUnlink: () => void;
}

const AccountRow = memo(function AccountRow({
  account,
  onUnlink,
}: AccountRowProps) {
  const { icon } = useBrandLogo(account.name);
  const [showMenu, setShowMenu] = useState(false);

  return (
    <View style={styles.accountRow}>
      <View style={styles.accountLogo}>
        {icon ? (
          <Image
            source={{ uri: icon }}
            style={styles.logo}
            contentFit="cover"
          />
        ) : (
          <View style={styles.logoFallback}>
            <Text style={styles.logoInitial}>
              {getInitialLetter(account.name)}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.accountInfo}>
        <Text style={styles.accountName}>{account.name}</Text>
        <Text style={styles.accountDate}>
          {formatRelativeDate(account.lastSyncedAt)}
        </Text>
      </View>
      <TouchableOpacity
        style={styles.moreButton}
        onPress={() => setShowMenu(!showMenu)}
      >
        <MoreIcon />
      </TouchableOpacity>
      {showMenu && (
        <View style={styles.menuPopup}>
          <TouchableOpacity
            style={styles.menuItem}
            onPress={() => {
              onUnlink();
              setShowMenu(false);
            }}
          >
            <Text style={styles.menuItemText}>Unlink</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
});

// Transaction row item for account section
interface AccountTransactionRowProps {
  transaction: Transaction;
}

const AccountTransactionRow = memo(function AccountTransactionRow({
  transaction,
}: AccountTransactionRowProps) {
  const merchantName = transaction.merchantName || transaction.name;
  const { icon } = useBrandLogo(merchantName);
  const categoryConfig = getCategoryConfig(transaction.category);

  return (
    <View style={styles.transactionRow}>
      <View style={styles.transactionLogo}>
        {icon ? (
          <Image
            source={{ uri: icon }}
            style={styles.transactionLogoImg}
            contentFit="cover"
          />
        ) : (
          <View style={styles.transactionLogoFallback}>
            <Text style={styles.transactionLogoInitial}>
              {getInitialLetter(merchantName)}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.transactionInfo}>
        <View style={styles.transactionHeader}>
          <Text style={styles.transactionName} numberOfLines={1}>
            {merchantName}
          </Text>
          <View
            style={[
              styles.categoryBadge,
              { backgroundColor: categoryConfig.bgColor },
            ]}
          >
            <Text style={styles.categoryText}>
              {categoryConfig.emoji} {categoryConfig.name}
            </Text>
          </View>
        </View>
        <Text style={styles.transactionDate}>
          {formatDate(transaction.date)}
        </Text>
      </View>
      <Text style={styles.transactionAmount}>
        {formatCurrency(transaction.amount)}
      </Text>
    </View>
  );
});

// Expandable account transactions section
interface AccountTransactionsSectionProps {
  account: AccountSummary;
  transactions: Transaction[];
}

const AccountTransactionsSection = memo(function AccountTransactionsSection({
  account,
  transactions,
}: AccountTransactionsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Get transactions for this account (max 5), filtering out invalid transactions
  const accountTransactions = useMemo(() => {
    return transactions
      .filter((tx) => tx && tx.id && tx.accountId === account.accountId)
      .slice(0, 5);
  }, [transactions, account.accountId]);

  if (accountTransactions.length === 0) {
    return null;
  }

  return (
    <View style={styles.accountSection}>
      <TouchableOpacity
        style={styles.accountSectionHeader}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <View style={styles.accountSectionLeft}>
          <ChevronIcon isOpen={isExpanded} />
          <Text style={styles.accountSectionTitle}>{account.name}</Text>
        </View>
        <Text style={styles.accountSectionBalance}>
          {formatCurrency(account.balance)}
        </Text>
      </TouchableOpacity>

      {isExpanded && (
        <View style={styles.accountTransactionsList}>
          {accountTransactions.map((tx, index) => (
            <View key={tx?.id || `tx-${index}`}>
              <AccountTransactionRow transaction={tx} />
              {index < accountTransactions.length - 1 && (
                <View style={styles.transactionDivider} />
              )}
            </View>
          ))}
        </View>
      )}
    </View>
  );
});

export default function AccountScreen() {
  const { financeData, isLoading, fetchFinanceData } = useFinanceDataStore();
  const [isUnlinking, setIsUnlinking] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("my-money-accounts");
    }, []),
  );

  const accounts = financeData?.accounts || [];
  const transactions = financeData?.transactions || [];

  // Get last update date from first account
  const lastUpdateDate = accounts[0]?.lastSyncedAt;

  const handleAddMore = () => {
    router.push({
      pathname: "/my-money/setup",
      params: { type: SubscriptionSetupType.MyMoney },
    });
  };

  const handleUnlink = async (accountId: string, accountName: string) => {
    const confirmed = await showConfirm({
      title: "Unlink Account",
      message: `Are you sure you want to unlink ${accountName}? This will remove all associated transactions.`,
      confirmText: "Unlink",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      setIsUnlinking(true);
      const response = await SubscriptionServices.unlinkAccount(accountId);
      if (response.data?.success) {
        // Refresh finance data after unlinking
        await fetchFinanceData();
        showSuccess(`${accountName} has been unlinked.`);
      } else {
        showError("Failed to unlink account. Please try again.");
      }
    } catch (error) {
      console.error("Error unlinking account:", error);
      showError("Failed to unlink account. Please try again.");
    } finally {
      setIsUnlinking(false);
    }
  };

  if (isLoading || isUnlinking) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
        {isUnlinking && (
          <Text style={styles.loadingText}>Unlinking account...</Text>
        )}
      </View>
    );
  }

  return (
    <View style={styles.content}>
      {/* Linked Accounts Card */}
      <View style={styles.linkedAccountsCard}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Account</Text>
          <Text style={styles.cardSubtitle}>
            {accounts.length} account{accounts.length !== 1 ? "s" : ""} linked
            {lastUpdateDate
              ? ` • Last updated ${formatRelativeDate(
                  lastUpdateDate,
                ).toLowerCase()}`
              : ""}
          </Text>
        </View>

        {accounts.length > 0 && (
          <View style={styles.accountsList}>
            {accounts.map((account) => (
              <AccountRow
                key={account.accountId}
                account={account}
                onUnlink={() => handleUnlink(account.accountId, account.name)}
              />
            ))}
          </View>
        )}

        <TouchableOpacity style={styles.addMoreButton} onPress={handleAddMore}>
          <PlusIcon />
          <Text style={styles.addMoreText}>Add more</Text>
        </TouchableOpacity>
      </View>

      {/* Account Transactions Sections */}
      {accounts.map((account) => (
        <AccountTransactionsSection
          key={account.accountId}
          account={account}
          transactions={transactions}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 12,
    paddingBottom: 100,
    width: SCREEN_WIDTH,
    alignSelf: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.background,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#6B7280",
  },
  // Linked Accounts Card
  linkedAccountsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    ...Shadows.card,
  },
  cardHeader: {
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
    paddingBottom: 12,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
  },
  accountsList: {
    gap: 16,
    marginBottom: 16,
  },
  accountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  accountLogo: {
    width: 48,
    height: 48,
  },
  logo: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  logoFallback: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
  },
  logoInitial: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  accountInfo: {
    flex: 1,
    gap: 2,
  },
  accountName: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  accountDate: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
  },
  moreButton: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  menuPopup: {
    position: "absolute",
    right: 0,
    top: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
    zIndex: 10,
  },
  menuItem: {
    paddingVertical: 8,
  },
  menuItemText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  addMoreButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
  },
  addMoreText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
  },
  // Account Transactions Section
  accountSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    ...Shadows.card,
  },
  accountSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderColor: Colors.border,
  },
  accountSectionLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  chevronContainer: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
    transform: [{ rotate: "90deg" }],
  },
  chevronOpen: {
    transform: [{ rotate: "90deg" }],
  },
  accountSectionTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  accountSectionBalance: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
  },
  accountTransactionsList: {
    marginTop: 16,
    gap: 8,
  },
  transactionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 12,
  },
  transactionLogo: {
    width: 48,
    height: 48,
  },
  transactionLogoImg: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  transactionLogoFallback: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
  },
  transactionLogoInitial: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  transactionInfo: {
    flex: 1,
    gap: 4,
  },
  transactionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  transactionName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    flexShrink: 1,
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 20,
  },
  categoryText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#000000",
  },
  transactionDate: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  transactionAmount: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  transactionDivider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
});
